import { CadCommandType } from '../../../command/CadCommandType';
import type { FeatureReferenceBinding } from '../../binding/FeatureReferenceBinding';
import { createFeatureReferenceBinding } from '../../../utils/feature/createFeatureReferenceBinding';
import { FilletFeatureDefinitionModel } from '../../definition/fillet/FilletFeatureDefinition';
import { FilletFeaturePrototype } from '../../prototype/fillet/FilletFeaturePrototype';
import { FeatureCommandAdapter, type FeatureCommandBuildContext } from '../FeatureCommandAdapter';
import { buildFilletModelTopologyBindings } from '../../binding/buildFilletModelTopologyBindings';

/**
 * 圆角命令适配器。
 */
export class FilletCommandAdapter extends FeatureCommandAdapter<FilletFeaturePrototype> {
    /**
     * @inheritdoc
     */
    public supports(command: CadCommand): boolean {
        return command.type === CadCommandType.FILLET;
    }

    /**
     * @inheritdoc
     */
    public createPrototypeFromCommand(command: CadCommand): FilletFeaturePrototype | null {
        if (!this.supports(command)) {
            return null;
        }

        const params = structuredClone(command.params as FilletParams);
        const definition = FilletFeatureDefinitionModel.fromParams({
            commandParams: params,
        });
        const bindings = this.buildBindingsFromParams(params);
        return new FilletFeaturePrototype({
            featureId: command.id,
            definition,
            bindings,
            sourceSketchId: null,
        });
    }

    /**
     * @inheritdoc
     */
    public createCommandFromPrototype(
        context: FeatureCommandBuildContext<FilletFeaturePrototype>,
    ): CadCommand {
        const commandParams = context.prototype.getDefinition().getCommandParams();
        return {
            id: context.prototype.getFeatureId(),
            type: CadCommandType.FILLET,
            clientId: context.clientId,
            timestamp: context.timestamp ?? Date.now(),
            params: {
                ...commandParams,
                featureKind: 'fillet',
                blendMode: 'fillet',
                referenceBindings: context.prototype.getBindings().map((binding) => binding.serialize()),
            },
        };
    }

    /**
     * 从命令参数恢复输入绑定。
     *
     * @param params 命令参数。
     * @returns 输入绑定集合。
     */
    private buildBindingsFromParams(params: FilletParams): FeatureReferenceBinding[] {
        const serializedBindings = params.referenceBindings ?? buildFilletModelTopologyBindings({
            upstreamBodyObjectId: params.upstreamBodyObjectId,
            definition: params.definition,
        });
        return serializedBindings.map((binding) => createFeatureReferenceBinding(binding));
    }
}
